import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { z } from "zod";
import { ChevronRight, MessageCircle } from "lucide-react";
import { getAdminCase, updateCase } from "@/lib/clinic.functions";
import { SessionsPanel } from "@/components/admin/cases/sessions-panel";
import { ReportsPanel } from "@/components/admin/cases/reports-panel";
import { CheckpointsPanel } from "@/components/admin/cases/checkpoints-panel";
import { FormsPanel } from "@/components/admin/cases/forms-panel";
import { NotesPanel } from "@/components/admin/cases/notes-panel";
import { ProfilePanel } from "@/components/admin/cases/profile-panel";
import { ProgramCard } from "@/components/admin/program-card";
import { useQuery } from "@tanstack/react-query";
import { getMyRole } from "@/lib/dr-sara.functions";

const caseQ = (id: string) => queryOptions({ queryKey: ["admin", "case", id], queryFn: () => getAdminCase({ data: { id } }) });

const searchSchema = z.object({ tab: z.enum(["sessions","reports","checkpoints","forms","profile","notes"]).optional() });

export const Route = createFileRoute("/_authenticated/admin/cases/$id")({
  head: () => ({ meta: [
    { title: "ملف الحالة | إدارة د. سارة النحّاس" },
    { name: "description", content: "متابعة جلسات الحالة وتقاريرها وبرامج الاستشارة ضمن إدارة د. سارة النحّاس." },
    { property: "og:title", content: "ملف الحالة | إدارة د. سارة النحّاس" },
    { property: "og:description", content: "متابعة جلسات الحالة وتقاريرها وبرامج الاستشارة ضمن إدارة د. سارة النحّاس." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
  ] }),
  validateSearch: searchSchema,
  loader: ({ context, params }) => context.queryClient.ensureQueryData(caseQ(params.id)),
  errorComponent: ({ error }) => <p className="p-6 text-muted-foreground">{String(error)}</p>,
  notFoundComponent: () => <p className="p-6">الحالة غير موجودة</p>,
  component: CaseDetail,
});

const TABS = [
  { id: "sessions", label: "الجلسات" },
  { id: "reports", label: "التقارير" },
  { id: "checkpoints", label: "نقاط المتابعة" },
  { id: "forms", label: "الاستمارات" },
  { id: "profile", label: "ملف الطفل" },
  { id: "notes", label: "ملاحظات سرّية" },
] as const;

function CaseDetail() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const tab = (search.tab ?? "sessions") as (typeof TABS)[number]["id"];
  const { data } = useSuspenseQuery(caseQ(id));
  const qc = useQueryClient();
  const updateFn = useServerFn(updateCase);
  const { data: capabilities } = useQuery({ queryKey: ["me", "role"], queryFn: () => getMyRole() });

  if (!data) return <p>—</p>;
  const {
    case: c, parent, sessions, reports, attempts, intake, bookings,
    pillars = [], screener_top_weak = [], problem_suggested_pillars = [], presenting_problem_slugs = [],
  } = data as any;

  async function setStatus(newStatus: string) {
    await updateFn({ data: { id, status: newStatus as any } });
    qc.invalidateQueries({ queryKey: ["admin", "case", id] });
    qc.invalidateQueries({ queryKey: ["admin", "cases"] });
    toast.success("تم التحديث");
  }

  return (
    <div>
      <nav className="mb-4 flex items-center gap-2 text-xs text-muted-foreground">
        <Link to="/admin/cases" className="hover:text-foreground">الحالات</Link>
        <ChevronRight className="h-3 w-3 rotate-180" />
        <span className="text-foreground">{c.child_alias}</span>
      </nav>

      <header className="mb-6 flex flex-wrap items-start justify-between gap-4 rounded-2xl bg-card p-6 shadow-soft">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-foreground">{c.child_alias}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {parent?.full_name ?? "—"} · {parent?.phone_whatsapp ?? "—"} · {parent?.country ?? "—"}
          </p>
          {(c.tags ?? []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {c.tags.map((t: string) => (
                <span key={t} className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold">{t}</span>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={c.status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-full border border-input bg-background px-3 py-2 text-sm font-semibold"
          >
            <option value="active">نشطة</option>
            <option value="paused">موقوفة</option>
            <option value="closed">مغلقة</option>
          </select>
          {parent?.phone_whatsapp && (
            <a href={`https://wa.me/${parent.phone_whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-input bg-card px-4 py-2 text-sm font-bold hover:bg-accent/15">
              <MessageCircle className="h-4 w-4" /> واتساب
            </a>
          )}
        </div>
      </header>

      {bookings && bookings.length > 0 && (
        <section className="mb-6 rounded-2xl bg-card p-5 shadow-soft">
          <p className="kicker text-modern-orange">الحجوزات المرتبطة</p>
          <ul className="mt-3 space-y-2 text-sm">
            {bookings.map((b: any) => (
              <li key={b.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-background/60 px-3 py-2">
                <div className="min-w-0">
                  <p className="font-display font-bold text-foreground">{b.service?.title_ar ?? "—"}</p>
                  <p className="text-xs text-muted-foreground">
                    {b.scheduled_at
                      ? new Intl.DateTimeFormat("ar-u-nu-latn", { day: "numeric", month: "long", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true }).format(new Date(b.scheduled_at))
                      : "بدون موعد"}
                  </p>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="rounded-full bg-primary/15 px-2 py-0.5 font-semibold">{b.status}</span>
                  <span className="rounded-full bg-soft-green/25 px-2 py-0.5 font-semibold">{b.payment_status}</span>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {capabilities?.isAdmin && bookings?.map((booking: { id: string }) => <ProgramCard key={booking.id} bookingId={booking.id} phone={parent?.phone_whatsapp} childName={c.child_alias === "—" ? undefined : c.child_alias} />)}

      <nav className="mb-4 flex flex-wrap gap-2 border-b border-border pb-2">
        {TABS.map((t) => (
          <Link
            key={t.id}
            to="/admin/cases/$id"
            params={{ id }}
            search={{ tab: t.id }}
            className={`rounded-full px-4 py-2 font-display text-sm font-bold transition-colors ${
              tab === t.id ? "bg-primary/25 text-foreground" : "text-muted-foreground hover:bg-accent/15"
            }`}
          >
            {t.label}
          </Link>
        ))}
      </nav>

      {tab === "sessions" && (
        <SessionsPanel
          caseId={id}
          sessions={sessions}
          pillars={pillars}
          screenerTopWeak={screener_top_weak}
          problemSuggestedPillars={problem_suggested_pillars}
          presentingProblemSlugs={presenting_problem_slugs}
        />
      )}
      {tab === "reports" && <ReportsPanel caseId={id} reports={reports} />}
      {tab === "checkpoints" && <CheckpointsPanel caseId={id} />}
      {tab === "forms" && <FormsPanel caseId={id} attempts={attempts} intake={intake} />}
      {tab === "profile" && <ProfilePanel caseId={id} />}
      {tab === "notes" && <NotesPanel c={c} />}
    </div>
  );
}